import React, { useContext, useState } from "react";
import {
  Avatar,
  Button,
  Dropdown,
  Flex,
  MenuProps,
  Skeleton,
  Space,
  Tag,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  MessageOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import InfiniteScroll from "react-infinite-scroll-component";
import { IUserInfo } from "../../../../../utilFunctions/CommonInterfaces/userInterface";
import { CommentInput } from "./CommentInput";
import relativeTime from "dayjs/plugin/relativeTime";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { Comment } from "./ChannelCommentHome";
import { UserInfoContext } from "Context/CommonContext";
import QuestionTextProvider from "Components/CommonComponents/QuestionTextProvider";

dayjs.extend(relativeTime);
dayjs.extend(isSameOrAfter);

export function formatCommentDate(timestamp: string): string {
  const date = dayjs(timestamp);
  const oneWeekAgo = dayjs().subtract(7, "day");

  if (date.isSameOrAfter(oneWeekAgo)) {
    return date.fromNow();
  } else {
    return date.format("MMM D, YYYY");
  }
}

export const getInitials = (name: string | null | undefined): string => {
  if (!name) return "";
  const nameParts = name.trim().split(" ");
  if (nameParts.length === 1) {
    return nameParts[0].charAt(0).toUpperCase();
  }
  const firstInitial = nameParts[0].charAt(0);
  const lastInitial = nameParts[nameParts.length - 1].charAt(0);
  return `${firstInitial}${lastInitial}`.toUpperCase();
};

export const generateColor = (name: string | null | undefined): string => {
  const COLORS = [
    "#ff7875",
    "#ff9c6e",
    "#ffd666",
    "#c6e949",
    "#95de64",
    "#5cdbd3",
    "#69b1ff",
    "#85a5ff",
    "#b37feb",
    "#f759ab",
  ];
  if (!name) return "#cccccc";
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash % COLORS.length);
  return COLORS[index];
};

const { Text } = Typography;

interface CommentPostProps {
  comment: Comment;
  onReply: (text: string, parentId: string | null) => void;
  onEdit: (text: string, commentId: string) => void;
  onDelete: (commentId: string, parentCommentId: string | null) => void;
  activeReplyId: string | null;
  activeEditId: string | null;
  setActiveReplyId: (id: string | null) => void;
  setActiveEditId: (id: string | null) => void;
  user: IUserInfo;
  fetchReplies: (parentId: string, isNext?: boolean) => void;
  loadingRepliesFor: string | null;
}

export const CommentPost: React.FC<CommentPostProps> = ({
  comment,
  onReply,
  onEdit,
  onDelete,
  activeReplyId,
  activeEditId,
  setActiveReplyId,
  setActiveEditId,
  user,
  fetchReplies,
  loadingRepliesFor,
}) => {
  const userInfo = useContext<IUserInfo>(UserInfoContext);
  const {
    author,
    timestamp,
    content,
    isOwnPost,
    replies,
    id,
    replyCount,
    parentCommentId,
  } = comment;
  const [showReplies, setShowReplies] = useState(false);
  const isReplying = activeReplyId === id;
  const isEditing = activeEditId === id;
  const isLoadingMyReplies = loadingRepliesFor === id;

  const canDelete = user.fexRole != "Student" || isOwnPost;

  const handleToggleReplies = () => {
    if (!showReplies && (!replies || replies.length === 0)) {
      fetchReplies(id);
    }
    setShowReplies(!showReplies);
  };

  const menuItems: MenuProps["items"] = [];
  if (isOwnPost) {
    menuItems.push({
      key: "1",
      label: "Edit",
      icon: <EditOutlined />,
      onClick: () => setActiveEditId(id),
    });
  }
  if (canDelete) {
    menuItems.push({
      key: "2",
      label: "Delete",
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () =>
        onDelete(id, parentCommentId == null ? null : parentCommentId),
    });
  }

  // const header = (
  //   <Flex justify="space-between" align="center">
  //     <Space>
  //       <Text strong>{author.name}</Text>
  //       <Tag
  //         bordered={false}
  //         color={author.role === "STUDENT" ? "gold" : "cyan"}
  //       >
  //         {author.role === "STUDENT" ? "Student" : author.role || "Faculty"}
  //       </Tag>
  //     </Space>
  //     <Space>
  //       <Text type="secondary">{formatCommentDate(timestamp)}</Text>
  //       {menuItems.length > 0 && (
  //         <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
  //           <Button type="text" shape="circle" icon={<MoreOutlined />} />
  //         </Dropdown>
  //       )}
  //     </Space>
  //   </Flex>
  // );

  return (
    <Flex gap="middle">
      <Avatar
        style={{ backgroundColor: generateColor(author.name), marginTop: 6 }}
        src={author.imageUrl}
        alt={`${author.name}'s profile picture`}
      >
        {getInitials(author.name)}
      </Avatar>
      <Flex vertical flex={1}>
        <div
          style={{
            background: "#f9f9f9",
            padding: "12px 16px",
            borderRadius: "12px",
            border: "1px solid #f0f0f0",
          }}
        >
          <Flex
            justify="space-between"
            align="center"
            style={{ marginBottom: "6px" }}
            wrap="wrap"
          >
            <Space size="small" wrap>
              <Text style={{ fontWeight: 600, color: "#262626" }}>
                {author.name}
              </Text>
              <Tag
                bordered={false}
                style={{
                  fontSize: "10px",
                  borderRadius: "4px",
                  backgroundColor:
                    author.role === "STUDENT" ? "#fff7e6" : "#e6fffb",
                  color: author.role === "STUDENT" ? "#d48806" : "#08979c",
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
              >
                {author.role === "STUDENT"
                  ? "Student"
                  : isOwnPost
                  ? userInfo.fexRole
                  : author.role || "Faculty"}
              </Tag>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                {formatCommentDate(timestamp)}
              </Text>
            </Space>
            {menuItems.length > 0 && (
              <Dropdown
                menu={{ items: menuItems }}
                trigger={["click"]}
                placement="bottomRight"
                getPopupContainer={(triggerNode) => triggerNode.parentElement!}
              >
                <Button
                  type="text"
                  shape="circle"
                  size="small"
                  icon={
                    <MoreOutlined
                      style={{ color: "#36454F", fontWeight: 600 }}
                    />
                  }
                />
              </Dropdown>
            )}
          </Flex>
          {isEditing ? (
            <CommentInput
              initialValue={content}
              buttonText="Save"
              onSubmit={(text) => {
                onEdit(text, id);
                setActiveEditId(null);
              }}
              user={user}
            />
          ) : (
            <div
              className="channel-rich-content"
              style={{
                margin: 0,
                color: "#595959",
                fontSize: "14px",
                lineHeight: "1.5",
              }}
            >
              <QuestionTextProvider
                content={content || ""}
              ></QuestionTextProvider>
            </div>
          )}
        </div>
        <Space>
          {comment.parentCommentId === null && !isReplying && (
            <Button
              type="text"
              size="small"
              onClick={() => setActiveReplyId(id)}
            >
              <Flex align="center" gap={4}>
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "16px" }}
                >
                  reply
                </span>
                <span>Reply</span>
              </Flex>
            </Button>
          )}
          {/* add reply count condition(replyCount > 0) here to show reply button only when there are replies */}
          {replyCount > 0 && (
            <Button
              type="link"
              size="small"
              onClick={handleToggleReplies}
              icon={<MessageOutlined />}
            >
              {showReplies ? "Hide" : `${replyCount} Replies`}
            </Button>
          )}
        </Space>

        {showReplies && (
          <div style={{ marginTop: "16px" }}>
            {isLoadingMyReplies && (!replies || replies.length === 0) ? (
              <Skeleton avatar paragraph={{ rows: 1 }} active />
            ) : (
              <InfiniteScroll
                dataLength={replies?.length || 0}
                next={() => fetchReplies(id, true)}
                hasMore={comment.hasMoreReplies ?? false}
                loader={
                  isLoadingMyReplies ? (
                    <Skeleton avatar paragraph={{ rows: 1 }} active />
                  ) : null
                }
                scrollableTarget="scrollableCommentDiv"
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  {replies?.map((reply) => (
                    <CommentPost
                      key={reply.id}
                      comment={reply}
                      onReply={onReply}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      activeReplyId={activeReplyId}
                      activeEditId={activeEditId}
                      setActiveReplyId={setActiveReplyId}
                      setActiveEditId={setActiveEditId}
                      user={user}
                      fetchReplies={fetchReplies}
                      loadingRepliesFor={loadingRepliesFor}
                    />
                  ))}
                </Space>
              </InfiniteScroll>
            )}
          </div>
        )}

        {isReplying && (
          <div style={{ marginTop: "16px" }}>
            <CommentInput
              buttonText="Post Reply"
              onSubmit={(text) => {
                onReply(text, id);
                setActiveReplyId(null);
              }}
              onCancel={() => setActiveReplyId(null)}
              user={user}
            />
          </div>
        )}
      </Flex>
    </Flex>
  );
};
